/* Raw Blend 2026 — RB · Sign-up pop-up (sections/rb-popup.liquid). No dependencies.
   - Opens after N seconds OR N% scroll OR exit intent (mouse heading for the tabs, desktop only),
     whichever comes first. Never while another drawer/dialog is open or the shopper is typing.
   - Remembers (localStorage + a cookie, both wrapped in try/catch): when it was last seen/closed, and
     whether this browser signed up. Closed = quiet for N days. Signed up = never again.
   - data-mode (per kind of page): open = the above; gentle = exit intent only, plus the teaser from the start;
     button = the teaser only.
   - After closing, a "Get $10 off" teaser sits bottom-left (the chat bubble is bottom-right). Phones: a gift
     tab tucked into the right edge just above the chat's tab, so the bottom-left corner (the left card's
     price in a grid, the product price) is always clear. It moves with the chat: it tucks away while the page
     scrolls down and while the chat is hidden (html.rb-chat-hide, from rb-main.js: the home hero, drawers),
     and comes back when scrolling stops. It slides out with its label for a moment ("peek") the first time
     it is really on screen (phones 400px and wider), on at most 3 pages per visit. Swiping the tab off the
     right edge hides the offer, like its x (which phones keep for keyboards and screen readers).
   - The Shopify customer form posts in the background (fetch) and the reply is read in place.
     If Shopify answers with a captcha page, it falls back to a normal post; the page then comes back
     with the result and the pop-up reopens to show it.
   - Theme editor: opens when the section (or a block) is selected, closes when deselected.
   Re-binds on shopify:section:load. */
(() => {
  if (window.RBPopup) { window.RBPopup.init(document); return; }

  const DAY = 864e5;
  const STORE_KEY = 'rb-popup';
  const SENT_KEY = 'rb-popup-sent';
  const TEASER_PAGES_KEY = 'rb-popup-teaser-pages';
  const TEASER_PAGES_MAX = 3;
  const mq = (q) => window.matchMedia(q).matches;
  const isPhone = () => mq('(max-width: 749px)');
  const reduced = () => mq('(prefers-reduced-motion: reduce)');

  /* ---- Storage: localStorage, mirrored into a cookie; every access is guarded ---- */
  const readStore = () => {
    try { const v = window.localStorage.getItem(STORE_KEY); if (v) return JSON.parse(v) || {}; } catch (e) { /* blocked */ }
    try {
      const m = document.cookie.match(/(?:^|;\s*)rb-popup=([^;]*)/);
      if (m) return JSON.parse(decodeURIComponent(m[1])) || {};
    } catch (e) { /* blocked */ }
    return {};
  };
  const writeStore = (data) => {
    const json = JSON.stringify(data);
    try { window.localStorage.setItem(STORE_KEY, json); } catch (e) { /* blocked */ }
    try { document.cookie = `${STORE_KEY}=${encodeURIComponent(json)}; path=/; max-age=${400 * 86400}; samesite=lax`; } catch (e) { /* blocked */ }
  };
  const session = {
    get(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { /* blocked */ } },
    del(k) { try { window.sessionStorage.removeItem(k); } catch (e) { /* blocked */ } },
  };

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const visible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length) && !el.closest('[hidden]');

  class Popup {
    constructor(root) {
      this.root = root;
      this.id = root.dataset.sectionId;
      this.dialog = root.querySelector('[data-rb-popup-dialog]');
      this.card = root.querySelector('[data-rb-popup-card]');
      this.teaser = root.querySelector('[data-rb-popup-teaser]');
      this.form = root.querySelector('form');
      const d = root.dataset;
      this.cfg = {
        mode: /^(gentle|button)$/.test(d.mode || '') ? d.mode : 'open',
        delay: parseFloat(d.delay) || 0,
        scroll: parseFloat(d.scroll) || 0,
        exit: d.exit === 'true',
        days: parseFloat(d.days) || 14,
        mobile: d.mobile !== 'false',
        teaser: d.teaser === 'true' && !!this.teaser,
        key: d.key || 'welcome',
        design: d.design === 'true' || !!(window.Shopify && window.Shopify.designMode),
      };
      this.msg = { invalid: d.msgInvalid || 'Please enter a valid email address.', generic: d.msgGeneric || 'Sorry, that didn\'t go through. Please try again.' };
      this.isOpen = false;
      this.offs = [];
      this.timers = [];
      this.bind();
      this.boot();
    }

    /* ---------- state ---------- */
    state() {
      const all = readStore();
      const camp = all.c && all.c.key === this.cfg.key ? all.c : { key: this.cfg.key };
      return { signed: all.signed || 0, camp };
    }
    save(patch) {
      if (this.cfg.design) return;
      const all = readStore();
      const camp = all.c && all.c.key === this.cfg.key ? all.c : { key: this.cfg.key };
      const next = { signed: all.signed || 0, c: camp };
      if ('signed' in patch) next.signed = patch.signed;
      Object.keys(patch).forEach((k) => { if (k !== 'signed') camp[k] = patch[k]; });
      writeStore(next);
    }
    recent(ts) { return ts && Date.now() - ts < this.cfg.days * DAY; }

    /* ---------- start-up ---------- */
    boot() {
      const mark = this.root.querySelector('[data-rb-popup-result]');
      const result = mark ? mark.dataset.rbPopupResult : '';
      const mine = session.get(SENT_KEY) === this.cfg.key;
      session.del(SENT_KEY);

      if (result === 'success') {
        this.save({ signed: Date.now() });
        this.setDone();
        if (mine) this.whenStyled().then(() => this.open({ reason: 'return' }));
        return;
      }
      if (result === 'error') {
        if (mine) { this.whenStyled().then(() => this.open({ reason: 'return', focus: 'email' })); return; }
        this.clearError();
      }
      if (this.cfg.design) return; /* the editor opens it on select */
      if (!this.cfg.mobile && isPhone()) return;

      const st = this.state();
      if (st.signed) return;
      const teaserOk = !this.recent(st.camp.teaserOff);
      if (this.recent(Math.max(st.camp.seen || 0, st.camp.closed || 0))) {
        if (teaserOk) this.showTeaser();
        return;
      }
      /* Product pages (by default), search, 404 and policies: never a timer or scroll opener. The corner
         button is there from the start so the offer can still be found; "gentle" pages also ask once as a
         computer shopper heads for the tabs to leave. */
      if (this.cfg.mode !== 'open') {
        if (this.cfg.mode === 'gentle') this.arm({ exitOnly: true });
        if (teaserOk) this.timers.push(setTimeout(() => this.showTeaser(), 4000));
        return;
      }
      this.arm();
    }

    arm(opts = {}) {
      const t0 = performance.now();
      const fire = (why) => { this.disarm(); this.tryOpen(why); };
      if (!opts.exitOnly && this.cfg.delay > 0) this.timers.push(setTimeout(() => fire('timer'), this.cfg.delay * 1000));
      if (!opts.exitOnly && this.cfg.scroll > 0) {
        const onScroll = () => {
          const max = document.documentElement.scrollHeight - window.innerHeight;
          if (max > 0 && (window.scrollY / max) * 100 >= this.cfg.scroll) fire('scroll');
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        this.offs.push(() => window.removeEventListener('scroll', onScroll));
      }
      if ((opts.exitOnly || this.cfg.exit) && mq('(hover: hover) and (pointer: fine)')) {
        const onOut = (e) => {
          if (e.relatedTarget || e.clientY > 12 || performance.now() - t0 < 4000) return;
          fire('exit');
        };
        document.addEventListener('mouseout', onOut);
        this.offs.push(() => document.removeEventListener('mouseout', onOut));
      }
      /* Fetch the photo quietly once the page has settled, so it is ready when the card opens. */
      const warm = () => this.warm();
      if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 5000 });
      else this.timers.push(setTimeout(warm, 3000));
    }
    disarm() {
      this.offs.forEach((off) => off());
      this.offs = [];
      this.timers.forEach((t) => clearTimeout(t));
      this.timers = [];
    }
    warm() { this.root.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; }); }

    /* Wait politely if a drawer, menu or other dialog is open, or the shopper is typing. */
    busyElsewhere() {
      const b = document.body;
      if (b.classList.contains('overflow-hidden') || b.classList.contains('rb-drawer-open')) return true;
      if ([...document.querySelectorAll('dialog[open]')].some((dl) => dl !== this.dialog)) return true;
      if (document.querySelector('details[open].menu-drawer-container, details[open] > .search-modal, .drawer.active, cart-drawer.active')) return true;
      const a = document.activeElement;
      if (a && a !== b && a.matches('input, textarea, select, [contenteditable="true"]') && !this.root.contains(a)) return true;
      return false;
    }
    tryOpen(why, tries = 0) {
      if (this.isOpen) return;
      if (this.busyElsewhere() && tries < 20) {
        this.timers.push(setTimeout(() => this.tryOpen(why, tries + 1), 4000));
        return;
      }
      this.whenStyled().then(() => this.open({ reason: why }));
    }
    whenStyled() {
      const link = this.root.parentElement && this.root.parentElement.querySelector('link[data-rb-popup-css]');
      if (!link || link.media === 'all' || link.sheet && link.media !== 'print') return Promise.resolve();
      return new Promise((res) => {
        link.addEventListener('load', () => res(), { once: true });
        setTimeout(res, 2500);
      });
    }

    /* ---------- open / close ---------- */
    open(opts = {}) {
      if (this.isOpen || !this.dialog) return;
      this.isOpen = true;
      this.disarm();
      this.hideTeaser(true);
      this.warm();
      if (!opts.editor && opts.reason !== 'return') this.save({ seen: Date.now() });
      this.lastFocus = opts.opener || document.activeElement;
      const dlg = this.dialog;
      dlg.classList.remove('is-closing', 'is-open');
      try { if (!dlg.open) dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); }
      document.documentElement.classList.add('rb-popup-lock');
      this.card.style.transform = '';
      const go = () => dlg.classList.add('is-open');
      if (opts.instant) go(); else requestAnimationFrame(() => requestAnimationFrame(go));

      /* Focus: the card itself (never pop the phone keyboard uninvited). A shopper who asked for it
         with the corner button on a computer lands straight in the first field. */
      let target = this.card;
      if (opts.focus === 'email') target = this.form && this.form.querySelector('input[type="email"]') || target;
      else if (opts.focus === 'first' && !isPhone()) target = this.firstField() || target;
      else if (this.root.classList.contains('is-done')) target = this.root.querySelector('[data-rb-popup-success]') || target;
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
    }
    close(opts = {}) {
      if (!this.isOpen) return;
      this.isOpen = false;
      const dlg = this.dialog;
      if (!opts.editor) this.save({ closed: Date.now() });
      dlg.classList.remove('is-open');
      dlg.classList.add('is-closing');
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        dlg.classList.remove('is-closing');
        try { dlg.close(); } catch (e) { dlg.removeAttribute('open'); }
        this.afterClose(opts);
      };
      this.card.addEventListener('transitionend', (e) => { if (e.target === this.card && e.propertyName === 'transform') finish(); });
      setTimeout(finish, reduced() ? 30 : 480);
    }
    /* Runs once the dialog is really shut (also when the browser closes it by itself). */
    afterClose(opts = {}) {
      document.documentElement.classList.remove('rb-popup-lock');
      this.card.style.transform = '';
      const signed = this.state().signed || this.root.classList.contains('is-done');
      let focusTeaser = false;
      if (!signed && (opts.editor ? this.cfg.teaser : !this.recent(this.state().camp.teaserOff))) {
        this.showTeaser();
        focusTeaser = this.lastFocus && this.teaser && this.teaser.contains(this.lastFocus);
      }
      const back = focusTeaser ? this.teaser.querySelector('[data-rb-popup-open]') : this.lastFocus;
      if (back && back !== document.body && document.contains(back) && typeof back.focus === 'function') {
        try { back.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      }
    }
    firstField() { return this.form && [...this.form.querySelectorAll('input:not([type="hidden"])')].find(visible); }

    /* ---------- teaser ---------- */
    showTeaser() {
      if (!this.cfg.teaser || !this.teaser) return;
      if (!this.cfg.mobile && isPhone() && !this.cfg.design) return;
      /* Phones: the gift disc shows on at most 3 pages per visit, then retires until the next visit. */
      if (isPhone() && !this.cfg.design && !this.teaserCounted) {
        const n = parseInt(session.get(TEASER_PAGES_KEY), 10) || 0;
        if (n >= TEASER_PAGES_MAX) return;
        session.set(TEASER_PAGES_KEY, String(n + 1));
        this.teaserCounted = true;
      }
      this.teaserWanted = true;
      this.bindTuck();
      /* Phones: the disc stacks on the chat bubble, which the chat app adds ~1-2s after the page loads.
         Wait for it (at most 5s), or the disc would appear in the corner and then jump up. */
      this.whenChat().then(() => {
        if (!this.teaserWanted || this.isOpen) return;
        this.teaser.hidden = false;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!this.teaserWanted) return;
          this.teaser.classList.add('is-in');
          this.maybePeek();
        }));
      });
    }
    whenChat() {
      const find = () => document.querySelector('body > button[aria-label="Open chat"]');
      if (!isPhone() || this.cfg.design || find()) return Promise.resolve();
      return new Promise((res) => {
        let t = 0;
        const mo = new MutationObserver(() => { if (find()) { mo.disconnect(); clearTimeout(t); res(); } });
        mo.observe(document.body, { childList: true });
        t = setTimeout(() => { mo.disconnect(); res(); }, 5000);
      });
    }
    /* Phones: the disc says what it is for a moment, then tucks its label away. Only once it is really
       on screen (not tucked, not behind a buy bar or drawer), or the moment is wasted. */
    maybePeek() {
      const t = this.teaser;
      if (this.peeked || !t || t.hidden || !t.classList.contains('is-in') || !isPhone()) return;
      if (t.classList.contains('is-tucked') || getComputedStyle(t).pointerEvents === 'none') return;
      this.peeked = true;
      t.classList.add('is-peek');
      setTimeout(() => t.classList.remove('is-peek'), reduced() ? 5000 : 3600);
    }
    /* Phones: the disc and the chat bubble are one stack in the bottom-right corner and move together.
       Tucked while the page scrolls down (the same rule as the bubble: past 160px, back ~0.9s after
       scrolling stops or straight away on scrolling up), and while rb-main.js hides the bubble. */
    bindTuck() {
      if (this.tuckOff || !this.teaser) return;
      const html = document.documentElement;
      const why = { scroll: false, chat: false };
      const apply = () => {
        const on = isPhone() && !this.cfg.design && (why.scroll || why.chat);
        this.teaser.classList.toggle('is-tucked', on);
        if (!on) this.maybePeek();
      };
      let lastY = window.scrollY;
      let settle = 0;
      const onScroll = () => {
        const y = window.scrollY;
        /* The "Get $10 off" label never sits over the words a shopper is scrolling to read: it tucks away
           as soon as the page moves. */
        if (Math.abs(y - lastY) > 4) this.teaser.classList.remove('is-peek');
        if (y > lastY + 4 && y > 160) { if (!why.scroll) { why.scroll = true; apply(); } }
        else if (y < lastY - 4 && why.scroll) { why.scroll = false; apply(); }
        lastY = y;
        clearTimeout(settle);
        settle = setTimeout(() => { why.scroll = false; apply(); }, 900);
      };
      const readChat = () => {
        const hid = html.classList.contains('rb-chat-hide');
        if (hid !== why.chat) { why.chat = hid; apply(); }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      const mo = new MutationObserver(readChat);
      mo.observe(html, { attributes: true, attributeFilter: ['class'] });
      const phone = window.matchMedia('(max-width: 749px)');
      const onMq = () => apply();
      if (phone.addEventListener) phone.addEventListener('change', onMq);
      why.chat = html.classList.contains('rb-chat-hide');
      apply();
      this.tuckOff = () => {
        window.removeEventListener('scroll', onScroll);
        mo.disconnect();
        clearTimeout(settle);
        if (phone.removeEventListener) phone.removeEventListener('change', onMq);
      };
    }
    /* Phones: the gift tab can be swiped off the right edge (outward, so it never meets the phone's own
       edge-swipe for "back", which runs inward) to hide the offer, as its x does. The tab follows the
       finger; less than 28px springs back. A swipe never opens the pop-up. */
    bindSwipe() {
      const t = this.teaser;
      if (!t) return;
      let x0 = null, y0 = 0, dx = 0, on = false;
      t.addEventListener('touchstart', (e) => {
        if (!isPhone() || !t.classList.contains('is-in') || e.touches.length > 1) return;
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; dx = 0; on = false;
      }, { passive: true });
      t.addEventListener('touchmove', (e) => {
        if (x0 === null) return;
        dx = e.touches[0].clientX - x0;
        const dy = e.touches[0].clientY - y0;
        if (!on && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) { on = true; t.classList.add('is-swiping'); }
        if (on) t.style.translate = `${Math.max(-10, dx)}px 0`;
      }, { passive: true });
      const end = () => {
        if (x0 === null) return;
        x0 = null;
        if (!on) return;
        on = false;
        this.swipedAt = Date.now();
        t.classList.remove('is-swiping');
        if (dx > 28) {
          t.style.translate = '90px 0';
          this.save({ teaserOff: Date.now() });
          this.hideTeaser();
          setTimeout(() => { t.style.translate = ''; }, 500);
        } else {
          t.style.translate = '';
        }
      };
      t.addEventListener('touchend', end);
      t.addEventListener('touchcancel', end);
    }
    hideTeaser(instant) {
      if (!this.teaser) return;
      this.teaserWanted = false;
      this.teaser.classList.remove('is-in');
      if (instant) { this.teaser.hidden = true; return; }
      setTimeout(() => { if (!this.teaser.classList.contains('is-in')) this.teaser.hidden = true; }, reduced() ? 0 : 400);
    }

    /* ---------- form ---------- */
    setDone() {
      this.root.classList.add('is-done');
      const q = (sel) => this.root.querySelector(sel);
      const fields = q('[data-rb-popup-fields]');
      const ok = q('[data-rb-popup-success]');
      const done = q('[data-rb-popup-done]');
      if (fields) fields.hidden = true;
      if (ok) ok.hidden = false;
      if (done) done.hidden = false;
      this.clearError();
      this.hideTeaser(true);
    }
    showError(text, input) {
      const box = this.root.querySelector('[data-rb-popup-error]');
      const span = this.root.querySelector('[data-rb-popup-error-text]');
      if (span) span.textContent = text;
      if (box) box.hidden = false;
      const email = input || (this.form && this.form.querySelector('input[type="email"]'));
      if (email) {
        email.setAttribute('aria-invalid', 'true');
        const field = email.closest('.rb-popup__field');
        if (field) {
          field.classList.add('is-error');
          field.classList.remove('is-shake');
          void field.offsetWidth;
          field.classList.add('is-shake');
        }
        if (input) input.focus();
      }
    }
    clearError() {
      const box = this.root.querySelector('[data-rb-popup-error]');
      if (box) box.hidden = true;
      this.root.querySelectorAll('.rb-popup__field.is-error').forEach((f) => f.classList.remove('is-error'));
      const email = this.form && this.form.querySelector('input[type="email"]');
      if (email) email.removeAttribute('aria-invalid');
    }
    async submit(e) {
      e.preventDefault();
      if (this.sending) return;
      const email = this.form.querySelector('input[type="email"]');
      if (email) email.value = email.value.trim();
      if (!email || !email.value || !email.checkValidity()) { this.showError(this.msg.invalid, email); return; }
      this.clearError();
      this.sending = true;
      this.root.classList.add('is-sending');
      const btn = this.form.querySelector('[type="submit"]');
      if (btn) btn.setAttribute('aria-busy', 'true');

      let outcome = 'unknown';
      let message = '';
      try {
        const res = await fetch(this.form.getAttribute('action') || '/contact', {
          method: 'POST',
          body: new FormData(this.form),
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        const url = res.url || '';
        if (/\/challenge\b/.test(url)) outcome = 'challenge';
        else {
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const id = this.form.id;
          const mark = (id && doc.getElementById(id) && doc.getElementById(id).querySelector('[data-rb-popup-result]')) || doc.querySelector('[data-rb-popup-result]');
          const r = mark ? mark.getAttribute('data-rb-popup-result') : '';
          if (r === 'success' || /[?&]customer_posted=true/.test(url)) outcome = 'success';
          else if (r === 'error') { outcome = 'error'; message = (mark.textContent || '').trim(); }
          else if (doc.querySelector('form[action*="/challenge"], .shopify-challenge__container, #g-recaptcha, .h-captcha')) outcome = 'challenge';
        }
      } catch (err) {
        outcome = 'network';
      }

      this.sending = false;
      this.root.classList.remove('is-sending');
      if (btn) btn.removeAttribute('aria-busy');

      if (outcome === 'success') {
        this.save({ signed: Date.now() });
        this.setDone();
        const ok = this.root.querySelector('[data-rb-popup-success]');
        if (ok) ok.focus({ preventScroll: true });
      } else if (outcome === 'error') {
        this.showError(message || this.msg.generic, email);
      } else if (outcome === 'network') {
        this.showError(this.msg.generic);
      } else {
        /* Captcha or an answer we can't read: hand over to a normal post. The page comes back with
           Shopify's result and the pop-up reopens to show it. */
        session.set(SENT_KEY, this.cfg.key);
        HTMLFormElement.prototype.submit.call(this.form);
      }
    }

    /* ---------- wiring ---------- */
    bind() {
      const dlg = this.dialog;
      if (!dlg) return;
      this.root.addEventListener('click', (e) => {
        const closer = e.target.closest('[data-rb-popup-close]');
        if (closer && this.root.contains(closer)) { e.preventDefault(); this.close({ editor: this.cfg.design }); return; }
        const opener = e.target.closest('[data-rb-popup-open]');
        if (opener) {
          e.preventDefault();
          if (this.swipedAt && Date.now() - this.swipedAt < 700) return; /* the end of a swipe, not a tap */
          this.open({ reason: 'teaser', focus: 'first', opener });
          return;
        }
        const hide = e.target.closest('[data-rb-popup-teaser-hide]');
        if (hide) { e.preventDefault(); this.save({ teaserOff: Date.now() }); this.hideTeaser(); return; }
        const copy = e.target.closest('[data-rb-popup-copy]');
        if (copy) {
          const code = this.root.querySelector('[data-rb-popup-code]');
          const text = code ? code.textContent.trim() : '';
          const done = () => { const was = copy.textContent; copy.textContent = copy.dataset.copied || 'Copied!'; setTimeout(() => { copy.textContent = was; }, 1800); };
          if (navigator.clipboard && text) navigator.clipboard.writeText(text).then(done, () => {});
        }
      });
      this.bindSwipe();
      /* Escape: close with the animation. If the browser closes it anyway, tidy up. */
      dlg.addEventListener('cancel', (e) => { e.preventDefault(); this.close({ editor: this.cfg.design }); });
      dlg.addEventListener('close', () => {
        if (!this.isOpen) return;
        this.isOpen = false;
        dlg.classList.remove('is-open', 'is-closing');
        if (!this.cfg.design) this.save({ closed: Date.now() });
        this.afterClose({ editor: this.cfg.design });
      });
      /* Keep Tab inside the card. */
      dlg.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        const items = [...this.card.querySelectorAll(FOCUSABLE)].filter(visible);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        const a = document.activeElement;
        if (e.shiftKey && (a === first || a === this.card || !this.card.contains(a))) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && (a === last || !this.card.contains(a))) { e.preventDefault(); first.focus(); }
      });
      if (this.form) {
        this.form.noValidate = true;
        this.form.addEventListener('submit', (e) => this.submit(e));
        this.form.querySelectorAll('.rb-popup__input').forEach((input) => {
          input.addEventListener('input', () => {
            const field = input.closest('.rb-popup__field');
            if (field && field.classList.contains('is-error') && input.checkValidity()) this.clearError();
          });
        });
      }
      /* Phones: drag the sheet down to dismiss it. */
      let y0 = null;
      let dy = 0;
      this.card.addEventListener('touchstart', (e) => {
        if (!isPhone() || e.touches.length !== 1 || e.target.closest('input, button, a, label, textarea, select')) return;
        const body = this.card.querySelector('.rb-popup__body');
        if (body && body.scrollTop > 0) return;
        y0 = e.touches[0].clientY;
        dy = 0;
      }, { passive: true });
      this.card.addEventListener('touchmove', (e) => {
        if (y0 === null) return;
        dy = Math.max(0, e.touches[0].clientY - y0);
        if (dy > 4) {
          this.card.style.transition = 'none';
          this.card.style.transform = `translateY(${dy}px)`;
        }
      }, { passive: true });
      const release = () => {
        if (y0 === null) return;
        y0 = null;
        this.card.style.transition = '';
        if (dy > 90) { this.card.style.transform = ''; this.close(); }
        else this.card.style.transform = '';
      };
      this.card.addEventListener('touchend', release);
      this.card.addEventListener('touchcancel', release);
    }

    destroy() {
      this.disarm();
      if (this.tuckOff) { this.tuckOff(); this.tuckOff = null; }
      if (this.isOpen) {
        this.isOpen = false;
        try { this.dialog.close(); } catch (e) { /* ignore */ }
        document.documentElement.classList.remove('rb-popup-lock');
      }
    }
  }

  const instances = new Map();
  let selected = null;
  const init = (scope) => {
    (scope || document).querySelectorAll('[data-rb-popup]:not([data-rb-bound])').forEach((root) => {
      root.setAttribute('data-rb-bound', '');
      const p = new Popup(root);
      instances.set(p.id, p);
      if (selected && selected === p.id) p.open({ editor: true, instant: true });
    });
  };
  const bySection = (e) => e.detail && instances.get(e.detail.sectionId);

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    const p = bySection(e);
    if (p) { p.destroy(); instances.delete(p.id); }
  });
  const select = (e) => { const p = bySection(e); if (p) { selected = p.id; p.open({ editor: true }); } };
  const deselect = (e) => { const p = bySection(e); if (p) { selected = null; p.close({ editor: true }); } };
  document.addEventListener('shopify:section:select', select);
  document.addEventListener('shopify:block:select', select);
  document.addEventListener('shopify:section:deselect', deselect);

  window.RBPopup = { init, instances };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
})();
